/**
 * Background-music player.
 *
 *   • Auto-discovers tracks named `/music/music1.mp3`, `/music/music2.mp3`,
 *     … by probing sequential numbers via HEAD requests until the first 404.
 *     This lets the user drop new files in /public/music without touching
 *     code — they just need to be named `music<N>.mp3`.
 *
 *   • Plays tracks in order. When one ends, the next starts. After the
 *     last, wraps back to track 1.
 *
 *   • A DynamicsCompressor sits in front of the music gain so tracks with
 *     different mastered loudness all hit roughly the same perceived
 *     volume. The compressor is tuned wide (low threshold + moderate ratio)
 *     so it normalises differences without colouring the music.
 *
 *   • Volume persists to localStorage. Default 0.20 (quiet — players can
 *     crank it up via the in-game audio panel).
 *
 *   • Browsers require a user gesture before audio can play. The first
 *     `start()` call waits for a click/tap if `audioCtx.state === 'suspended'`.
 */

const STORAGE_KEY = 'wol.volume.music';
const DEFAULT_VOLUME = 0.20;
const MUSIC_BASE = '/music/music';
const MUSIC_EXT = '.mp3';
const MAX_PROBE = 50; // safety cap when auto-discovering

let ctx: AudioContext | null = null;
let audioEl: HTMLAudioElement | null = null;
let trackSource: MediaElementAudioSourceNode | null = null;
let compressor: DynamicsCompressorNode | null = null;
let musicGain: GainNode | null = null;

let trackCount: number | null = null;
let currentTrack = 0; // 0-based index; the actual file is music{currentTrack+1}.mp3
let musicVolume: number = (() => {
  if (typeof window === 'undefined') return DEFAULT_VOLUME;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v !== null) {
      const n = parseFloat(v);
      if (Number.isFinite(n) && n >= 0 && n <= 1) return n;
    }
  } catch { /* localStorage disabled */ }
  return DEFAULT_VOLUME;
})();
let muted = false;
let started = false;

/** Discover how many sequential music tracks exist starting at music1. */
async function discoverTrackCount(): Promise<number> {
  // Probe music1.mp3 .. musicN.mp3 sequentially. Stop on first non-200.
  for (let i = 1; i <= MAX_PROBE; i++) {
    const url = `${MUSIC_BASE}${i}${MUSIC_EXT}`;
    try {
      const res = await fetch(url, { method: 'HEAD' });
      if (!res.ok) return i - 1;
    } catch {
      return i - 1;
    }
  }
  return MAX_PROBE;
}

/**
 * Build the Web Audio graph once (single AudioContext, single Audio element).
 * Subsequent track changes just swap the `audioEl.src`.
 */
function buildGraph(): boolean {
  if (typeof window === 'undefined') return false;
  if (audioEl && ctx) return true;
  const Ctor = (window as Window & {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
  }).AudioContext
    ?? (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return false;
  try {
    ctx = new Ctor();
  } catch { return false; }
  audioEl = new Audio();
  audioEl.crossOrigin = 'anonymous';
  audioEl.preload = 'auto';
  audioEl.addEventListener('ended', advanceToNext);

  trackSource = ctx.createMediaElementSource(audioEl);
  compressor = ctx.createDynamicsCompressor();
  // Tuned for cross-track loudness consistency:
  //   • threshold -22 dB → starts compressing well before clipping
  //   • knee 8 dB → soft transition into compression (transparent)
  //   • ratio 4 → moderate; pulls loud peaks down a few dB
  //   • attack 5 ms, release 250 ms → catches transients but lets musical
  //     phrases breathe
  compressor.threshold.value = -22;
  compressor.knee.value = 8;
  compressor.ratio.value = 4;
  compressor.attack.value = 0.005;
  compressor.release.value = 0.25;
  musicGain = ctx.createGain();
  musicGain.gain.value = muted ? 0 : musicVolume;
  trackSource.connect(compressor);
  compressor.connect(musicGain);
  musicGain.connect(ctx.destination);
  return true;
}

function advanceToNext(): void {
  if (!audioEl || trackCount === null || trackCount === 0) return;
  currentTrack = (currentTrack + 1) % trackCount;
  audioEl.src = `${MUSIC_BASE}${currentTrack + 1}${MUSIC_EXT}`;
  audioEl.play().catch(() => undefined);
}

/**
 * Begin music playback. Idempotent — safe to call multiple times. MUST
 * be called from within a user-gesture handler (e.g. a button click) so
 * the AudioContext is allowed to start per browser autoplay rules.
 *
 * Currently invoked from the "Démarrer la partie" button click — music
 * plays continuously from that point through the entire match.
 */
export function startMusic(): void {
  if (started) return;
  started = true;
  void beginPlayback();
}

async function beginPlayback(): Promise<void> {
  if (!buildGraph()) return;
  if (ctx && ctx.state === 'suspended') {
    try { await ctx.resume(); } catch { /* still suspended */ }
  }
  trackCount = await discoverTrackCount();
  if (!audioEl || trackCount === 0) return;
  currentTrack = 0;
  audioEl.src = `${MUSIC_BASE}1${MUSIC_EXT}`;
  audioEl.play().catch(() => undefined);
}

/**
 * Pause background music. Idempotent — safe to call when music has
 * never started or is already paused. Used by `unmountInGame` so the
 * soundtrack stops the moment the player leaves a match instead of
 * trailing on into the home hub / profile pages. The next call to
 * `ensureMusicPlaying` resumes from wherever the track was.
 */
export function pauseMusic(): void {
  if (!audioEl) return;
  if (!audioEl.paused) audioEl.pause();
}

/**
 * Ensure background music is actually playing right now.
 *
 *   • Never started? → identical to `startMusic()` — kicks the whole
 *     pipeline off and begins track 1.
 *   • Already started but paused? → resume the AudioContext (in case
 *     it suspended itself when the tab was backgrounded) and call
 *     `play()` again on the audio element. Picks up wherever the
 *     track was when it got paused.
 *   • Already playing? → no-op.
 *
 * Called from `mountInGame` so the music resumes when a player rejoins
 * a match they had left (the previous session's audio element may have
 * been paused by the browser when the tab lost focus / was closed, and
 * the original `startMusic()` is short-circuited by the `started` flag).
 *
 * Like `startMusic`, this should ideally fire inside a user-gesture
 * handler so the AudioContext is allowed to resume.
 */
export function ensureMusicPlaying(): void {
  if (!started) {
    startMusic();
    return;
  }
  // Already initialised — just nudge it back to life if it stalled.
  if (ctx && ctx.state === 'suspended') {
    try { void ctx.resume(); } catch { /* will retry next gesture */ }
  }
  if (audioEl && audioEl.paused) {
    audioEl.play().catch(() => undefined);
  }
}

/** Set the music volume (0..1). Persists to localStorage. */
export function setMusicVolume(v: number): void {
  musicVolume = Math.max(0, Math.min(1, v));
  if (musicGain) musicGain.gain.value = muted ? 0 : musicVolume;
  try { window.localStorage.setItem(STORAGE_KEY, String(musicVolume)); }
  catch { /* localStorage disabled */ }
}

export function getMusicVolume(): number { return musicVolume; }

export function setMusicMuted(m: boolean): void {
  muted = m;
  if (musicGain) musicGain.gain.value = m ? 0 : musicVolume;
}

export function isMusicMuted(): boolean { return muted; }

/** Number of tracks discovered, or null if discovery hasn't run yet. */
export function getMusicTrackCount(): number | null { return trackCount; }

/** 1-based index of the currently-playing track, or null if not started. */
export function getCurrentMusicIndex(): number | null {
  return audioEl ? currentTrack + 1 : null;
}

/** Manually jump to the next track. Useful for a "skip" button. */
export function skipToNext(): void { advanceToNext(); }
